import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, MapPin, CalendarDays, FileSpreadsheet, Megaphone, Wallet, ShieldCheck, Building2, History, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HomeHero } from "@/pages/site/HomeHero";

const PLACES = ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Hobart", "Darwin", "Canberra", "Gold Coast", "Newcastle", "Cairns", "Geelong", "Townsville", "Sunshine Coast"];

const Marquee = () => (
  <section className="overflow-hidden border-y border-[#0B2D5B]/10 bg-[#0B2D5B] py-5 text-white" data-testid="home-marquee">
    <div className="marquee flex w-max gap-10 whitespace-nowrap">
      {[...PLACES, ...PLACES].map((p, i) => (
        <span key={i} className="font-display flex items-center gap-10 text-lg font-semibold text-white/80">{p}<span className="h-2 w-2 rotate-45 bg-[#FF8A00]" /></span>
      ))}
    </div>
  </section>
);

const INDUSTRIES = [
  ["Hospitality", "Cafés, restaurants & pubs", "/img/ind-hospitality.jpg"],
  ["Construction & trades", "Sites, crews & subbies", "/img/ind-construction.jpg"],
  ["Retail", "Stores & shopping centres", "/img/ind-retail.jpg"],
  ["Healthcare & aged care", "Clinics, care homes & NDIS", "/img/ind-healthcare.jpg"],
];

const Industries = () => (
  <section className="bg-white py-24 lg:py-32" data-testid="home-industries">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div><p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E07800]">Made for how Australia works</p>
          <h2 className="mt-4 max-w-2xl text-4xl font-extrabold tracking-tight text-[#0B2D5B] sm:text-5xl">From the morning coffee run to the night shift.</h2></div>
        <p className="max-w-sm text-slate-600">Whether your team pulls shots, pours concrete or cares for others — Clockaroo fits the way you roster, track and pay.</p>
      </div>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {INDUSTRIES.map(([t, d, img], i) => (
          <div key={t} className={`group relative h-[420px] overflow-hidden rounded-[2rem] ${i % 2 ? "lg:translate-y-10" : ""}`} data-testid={`industry-card-${i}`}>
            <img src={img} alt={t} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B2D5B] via-[#0B2D5B]/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-white">
              <p className="font-display text-xl font-bold">{t}</p><p className="mt-1 text-sm text-white/75">{d}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Tile = ({ to, icon: Icon, title, text, className = "", children, id }) => (
  <Link to={to} className={`group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] p-7 transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-white/[0.08] ${className}`} data-testid={`bento-${id}`}>
    <div className="flex items-start justify-between"><span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#00C389]/15 text-[#00C389]"><Icon className="h-5 w-5" /></span><ArrowUpRight className="h-5 w-5 text-white/30 transition-colors group-hover:text-amber-300" /></div>
    <h3 className="mt-6 text-xl font-bold text-white">{title}</h3>
    <p className="mt-2 max-w-md text-sm text-white/60">{text}</p>
    {children}
  </Link>
);

const Bento = () => (
  <section className="grain relative overflow-hidden bg-[#071A38] py-24 lg:py-32" data-testid="features-section">
    <div className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full bg-[#00C16B]/30 blur-[140px]" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-300">Everything in one place</p>
      <h2 className="mt-4 max-w-3xl text-4xl font-extrabold tracking-tight text-white sm:text-5xl">One app. Every shift. <span className="text-[#00C389]">No more paper timesheets.</span></h2>
      <div className="mt-14 grid gap-5 lg:grid-cols-6">
        <Tile id="time-clock" to="/time-clock" icon={Clock} title="One-tap time clock" text="Clock in, take a break and clock out with one big button. Server-verified times, every time." className="lg:col-span-4">
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="grid h-24 w-24 place-items-center rounded-full bg-[#00C16B] text-sm font-bold text-white shadow-[0_0_0_10px_rgba(0,195,137,0.15)]">Clock in</span>
            <span className="rounded-2xl bg-amber-400/90 px-5 py-3 text-sm font-bold text-[#0B2D5B]">Start break</span>
            <span className="font-mono-num text-4xl font-bold text-white/90">07:58</span>
          </div>
        </Tile>
        <Tile id="geofencing" to="/gps-geofencing" icon={MapPin} title="GPS geofencing" text="Pin each workplace and set the radius. Off-site clock-ins get blocked or flagged." className="lg:col-span-2">
          <div className="relative mx-auto mt-8 grid h-28 w-28 place-items-center"><span className="radar absolute inset-0 rounded-full border-2 border-[#00C389]" /><span className="radar-2 absolute inset-0 rounded-full border-2 border-[#00C389]" /><MapPin className="h-8 w-8 fill-amber-400 text-[#071A38]" /></div>
        </Tile>
        <Tile id="rosters" to="/rosters" icon={CalendarDays} title="Drag-and-drop rosters" text="Build the week, copy last week, publish in a tap." className="lg:col-span-2">
          <div className="mt-6 grid grid-cols-7 gap-1">{Array.from({ length: 21 }).map((_, i) => <span key={i} className={`h-5 rounded ${[2, 5, 9, 12, 16, 18].includes(i) ? "bg-amber-400" : i % 3 ? "bg-white/10" : "bg-[#00C389]/70"}`} />)}</div>
        </Tile>
        <Tile id="timesheets" to="/timesheets" icon={FileSpreadsheet} title="Automatic timesheets" text="Every clock event flows straight in. Review, approve, lock and export." className="lg:col-span-2" />
        <Tile id="announcements" to="/announcements" icon={Megaphone} title="Team announcements" text="Staff meetings, training and Christmas parties — with RSVPs and read receipts." className="lg:col-span-2">
          <div className="mt-5 flex gap-2">{["Attending", "Maybe", "Not attending"].map((r, i) => <span key={r} className={`rounded-full px-3 py-1 text-xs font-semibold ${i === 0 ? "bg-[#00C389] text-[#071A38]" : "bg-white/10 text-white/70"}`}>{r}</span>)}</div>
        </Tile>
        <Tile id="payroll" to="/payroll-reporting" icon={Wallet} title="Payroll & reporting" text="Your pay rules, penalties and overtime — labour costs and CSV / Excel exports ready for payroll." className="lg:col-span-6">
          <div className="mt-6 flex flex-wrap gap-2">{["Casual · Part-time · Full-time", "Weekend & public holiday rates", "Overtime", "Allowances"].map((c) => <span key={c} className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-medium text-white/80">{c}</span>)}<span className="flex items-center gap-1 rounded-full bg-amber-400 px-4 py-1.5 text-xs font-bold text-[#0B2D5B]"><Download className="h-3.5 w-3.5" />Export</span></div>
        </Tile>
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="bg-[#FBFAF6] py-24 lg:py-32" data-testid="home-steps">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <h2 className="max-w-2xl text-4xl font-extrabold tracking-tight text-[#0B2D5B] sm:text-5xl">Up and running before smoko.</h2>
      <div className="relative mt-16 grid gap-10 md:grid-cols-3">
        <div className="absolute left-0 right-0 top-7 hidden h-0.5 bg-gradient-to-r from-[#00C16B] via-[#FF8A00] to-[#00C16B] md:block" />
        {[["Register your business", "Add your ABN, time zone and working week."], ["Pin your workplaces", "Find each address on the map and set the geofence."], ["Invite your crew", "Email invites or a secure link — they're clocking in today."]].map(([t, d], i) => (
          <div key={t} className="relative">
            <span className="font-mono-num relative grid h-14 w-14 place-items-center rounded-2xl bg-[#0B2D5B] text-lg font-bold text-amber-300 shadow-lg">0{i + 1}</span>
            <h3 className="mt-6 text-xl font-bold text-[#0B2D5B]">{t}</h3><p className="mt-2 text-slate-600">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Values = () => (
  <section className="bg-white py-24" data-testid="home-values">
    <div className="mx-auto grid max-w-7xl gap-5 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
      {[[ShieldCheck, "Your data, kept separate", "Every business's data is strictly isolated."], [MapPin, "Privacy-first location", "Location is checked at clock events — with clear notice to staff."], [Building2, "Multi-site & multi-business", "Staff can work across sites, or across businesses."], [History, "Full audit trail", "Original records are never silently overwritten."]].map(([Icon, t, d]) => (
        <div key={t} className="rounded-[2rem] border border-slate-200 p-7 transition-shadow hover:shadow-xl">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-amber-50 text-[#E07800]"><Icon className="h-5 w-5" /></span>
          <h3 className="mt-5 font-bold text-[#0B2D5B]">{t}</h3><p className="mt-2 text-sm text-slate-600">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Cta = () => (
  <section className="relative overflow-hidden" data-testid="home-cta">
    <img src="/img/outback.jpg" alt="Australian outback at sunset" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#071A38] via-[#071A38]/80 to-[#071A38]/20" />
    <div className="relative mx-auto max-w-7xl px-4 py-28 sm:px-6 lg:px-8 lg:py-36">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-300">Clockaroo</p>
      <h2 className="mt-4 max-w-xl text-5xl font-extrabold tracking-tight text-white sm:text-6xl">In &amp; out.<br />Sorted.</h2>
      <p className="mt-5 max-w-md text-lg text-white/75">Start your free trial and set up your first workplace today. No credit card needed.</p>
      <div className="mt-9 flex flex-wrap gap-3">
        <Link to="/register"><Button size="lg" className="h-14 rounded-full bg-[#FF8A00] px-8 text-base font-bold text-[#071A38] hover:bg-amber-400" data-testid="cta-register-btn">Start free trial <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
        <Link to="/pricing"><Button size="lg" variant="outline" className="h-14 rounded-full border-white/40 bg-transparent px-8 text-base text-white hover:bg-white/10 hover:text-white" data-testid="cta-pricing-btn">See pricing</Button></Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  return <div data-testid="home-page"><HomeHero /><Marquee /><Industries /><Bento /><Steps /><Values /><Cta /></div>;
}
